/**
 * Dynamic location-background availability.
 *
 * Extends the static, hand-curated location list with locations the processCompanyLogos
 * Lambda (web-app/lambda/processCompanyLogos.ts) discovers from real job data and downloads
 * on its own — so a new market gets a background image without a frontend deploy. The Lambda
 * publishes what it currently has images for to {BASE_URL}/location-backgrounds/manifest.json;
 * this module fetches and caches that list server-side, and quietly falls back to the static
 * list alone if the manifest is missing or unreachable.
 */
import { BASE_URL } from '../constants/url-constants';
import { getNormalizedLocationEnglish } from './translate-location-for-header';
import { isRemoteScopeLocationLabel } from './remote-hub-scope';

/** Hand-curated locations that have always had a specific background image. */
export const STATIC_LOCATIONS_WITH_BACKGROUNDS = new Set([
  'Helsinki, Finland', 'Tampere, Finland', 'Turku, Finland', 'Oulu, Finland', 'Jyväskylä, Finland',
  'Lahti, Finland', 'Kuopio, Finland', 'Pori, Finland', 'Joensuu, Finland', 'Vaasa, Finland',
  'Lappeenranta, Finland', 'Rovaniemi, Finland', 'Seinäjoki, Finland', 'Hämeenlinna, Finland',
  'Kotka, Finland', 'Kouvola, Finland', 'Porvoo, Finland', 'Kokkola, Finland', 'Mikkeli, Finland',
  'Rauma, Finland', 'Espoo, Finland', 'Hanko, Finland', 'Vantaa, Finland', 'Kemi, Finland',
  'Remote',
]);

/**
 * Shared background-image key for every "Remote - X" scope hub ("Remote - Finland",
 * "Remote - Europe", any future scope). Unlike city photos, this one image is meant to repeat
 * across pages on purpose: it illustrates the *concept* of remote work rather than claiming to
 * depict a specific, unrelated place, so reusing it doesn't carry the same misleading-photo risk
 * discussed below for city locations.
 */
export const REMOTE_SCOPE_BACKGROUND_KEY = 'Remote';

const MANIFEST_URL = `${BASE_URL}/location-backgrounds/manifest.json`;
const MANIFEST_CACHE_TTL_MS = 30 * 60 * 1000; // Lambda only runs nightly — freshness isn't time-critical.

let cachedKnownLocations: Set<string> = STATIC_LOCATIONS_WITH_BACKGROUNDS;
let cachedAt = 0;

async function fetchDiscoveredLocations(): Promise<string[]> {
  const response = await fetch(MANIFEST_URL);
  if (!response.ok) return [];
  const data = await response.json();
  return Array.isArray(data) ? data.filter((v): v is string => typeof v === 'string') : [];
}

/**
 * Server-side only: the static known-good locations plus whatever the nightly Lambda run has
 * since discovered and downloaded. Cached in-process; never throws — a manifest fetch failure
 * (including "doesn't exist yet") just means falling back to the static list for this request.
 */
export async function getKnownLocationBackgroundSet(): Promise<Set<string>> {
  const now = Date.now();
  if (now - cachedAt < MANIFEST_CACHE_TTL_MS) {
    return cachedKnownLocations;
  }

  try {
    const discovered = await fetchDiscoveredLocations();
    cachedKnownLocations = new Set([...STATIC_LOCATIONS_WITH_BACKGROUNDS, ...discovered]);
  } catch (error) {
    console.warn(
      '[known-location-backgrounds] Failed to fetch manifest, using static list only:',
      error instanceof Error ? error.message : String(error)
    );
    cachedKnownLocations = STATIC_LOCATIONS_WITH_BACKGROUNDS;
  }
  cachedAt = now;
  return cachedKnownLocations;
}

/**
 * Resolves the location key to use for a background image, or null if none is available.
 * Any "Remote - X" scope label (in any locale) routes to the single shared REMOTE_SCOPE_BACKGROUND_KEY
 * image rather than a per-location lookup. Otherwise normalizes locale variants (Finnish/Swedish
 * city names, "Suomi") to English and requires an exact match: a city without its own image
 * returns null (the header falls back to its plain gradient) rather than reusing an unrelated
 * city's photo. Showing e.g. Rovaniemi's arctic skyline on a Vaasa page would misrepresent a real,
 * different place; that risk doesn't apply to the shared remote-work image since it never claims
 * to be a specific location.
 */
export function resolveBackgroundImageLocation(location: string, knownLocations: Set<string>): string | null {
  if (!location || typeof location !== 'string') return null;
  if (isRemoteScopeLocationLabel(location)) {
    return knownLocations.has(REMOTE_SCOPE_BACKGROUND_KEY) ? REMOTE_SCOPE_BACKGROUND_KEY : null;
  }
  const normalized = getNormalizedLocationEnglish(location);
  return knownLocations.has(normalized) ? normalized : null;
}
